import { Mail, Phone, MapPin, User } from "lucide-react";
import { COMPANY, PHONE, SITE } from "@/lib/siteConfig";
import { LegalPage, LegalSection } from "@/components/landing/LegalPage";

export default function Impressum() {
  return (
    <LegalPage
      eyebrow="Rechtliches"
      title="Impressum"
      intro="Angaben gemäß § 5 TMG (Telemediengesetz) sowie § 18 Abs. 2 MStV (Medienstaatsvertrag)."
      metaDescription={`Impressum von ${COMPANY.name}, Inhaber ${COMPANY.owner}, ${COMPANY.street}, ${COMPANY.postalCode} ${COMPANY.city}. Anbieterkennzeichnung gemäß § 5 TMG.`}
      testId="impressum-page"
    >
      <LegalSection title="Anbieter">
        <p className="not-italic">
          <strong className="text-slate-900">{COMPANY.name}</strong>
          <br />
          Inhaber: {COMPANY.owner}
        </p>
        <address className="not-italic flex items-start gap-3 mt-4">
          <MapPin className="h-4 w-4 text-[#EAB308] mt-1 shrink-0" />
          <span>
            {COMPANY.street}
            <br />
            {COMPANY.postalCode} {COMPANY.city}
            <br />
            {COMPANY.countryName}
          </span>
        </address>
      </LegalSection>

      <LegalSection title="Kontakt">
        <div className="flex items-start gap-3">
          <Phone className="h-4 w-4 text-[#EAB308] mt-1 shrink-0" />
          <div>
            <div className="text-slate-500 text-xs uppercase tracking-widest">Telefon</div>
            <a
              href={`tel:${PHONE.tel}`}
              className="font-semibold text-slate-900 hover:text-[#EAB308] transition"
              data-testid="impressum-phone"
            >
              {PHONE.display}
            </a>
            <div className="text-xs text-slate-500 mt-1">{PHONE.hours}</div>
          </div>
        </div>

        <div className="flex items-start gap-3 mt-4">
          <Mail className="h-4 w-4 text-[#EAB308] mt-1 shrink-0" />
          <div>
            <div className="text-slate-500 text-xs uppercase tracking-widest">E-Mail</div>
            <a
              href={`mailto:${COMPANY.email}`}
              className="font-semibold text-slate-900 hover:text-[#EAB308] transition"
              data-testid="impressum-email"
            >
              {COMPANY.email}
            </a>
          </div>
        </div>

        <div className="flex items-start gap-3 mt-4">
          <User className="h-4 w-4 text-[#EAB308] mt-1 shrink-0" />
          <div>
            <div className="text-slate-500 text-xs uppercase tracking-widest">Internet</div>
            <a
              href={SITE.url}
              className="font-semibold text-slate-900 hover:text-[#EAB308] transition"
              data-testid="impressum-web"
            >
              {SITE.url.replace(/^https?:\/\//, "")}
            </a>
          </div>
        </div>
      </LegalSection>

      <LegalSection title="Verantwortlich für den Inhalt nach § 18 Abs. 2 MStV">
        <p>
          {COMPANY.owner}
          <br />
          {COMPANY.street}, {COMPANY.postalCode} {COMPANY.city}
        </p>
      </LegalSection>

      <LegalSection title="Umsatzsteuer-Identifikationsnummer">
        <p>
          Umsatzsteuer-Identifikationsnummer gemäß § 27 a Umsatzsteuergesetz:
        </p>
        <p className="not-italic bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 inline-block font-mono text-slate-900 font-semibold">
          {COMPANY.vatId}
        </p>
      </LegalSection>

      <LegalSection title="Streitschlichtung">
        <p>
          Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit:{" "}
          <a
            href="https://ec.europa.eu/consumers/odr/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#0A2340] font-semibold underline underline-offset-4 hover:text-[#EAB308] transition"
          >
            https://ec.europa.eu/consumers/odr/
          </a>
          . Unsere E-Mail-Adresse finden Sie oben im Impressum.
        </p>
        <p>
          Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.
        </p>
      </LegalSection>

      <LegalSection title="Haftung für Inhalte">
        <p>
          Als Diensteanbieter sind wir gemäß § 7 Abs. 1 TMG für eigene Inhalte auf diesen Seiten nach den allgemeinen Gesetzen verantwortlich. Nach §§ 8 bis 10 TMG sind wir als Diensteanbieter jedoch nicht verpflichtet, übermittelte oder gespeicherte fremde Informationen zu überwachen oder nach Umständen zu forschen, die auf eine rechtswidrige Tätigkeit hinweisen.
        </p>
        <p>
          Verpflichtungen zur Entfernung oder Sperrung der Nutzung von Informationen nach den allgemeinen Gesetzen bleiben hiervon unberührt. Eine diesbezügliche Haftung ist jedoch erst ab dem Zeitpunkt der Kenntnis einer konkreten Rechtsverletzung möglich. Bei Bekanntwerden von entsprechenden Rechtsverletzungen werden wir diese Inhalte umgehend entfernen.
        </p>
      </LegalSection>

      <LegalSection title="Haftung für Links">
        <p>
          Unser Angebot enthält gegebenenfalls Links zu externen Websites Dritter, auf deren Inhalte wir keinen Einfluss haben. Deshalb können wir für diese fremden Inhalte auch keine Gewähr übernehmen. Für die Inhalte der verlinkten Seiten ist stets der jeweilige Anbieter oder Betreiber der Seiten verantwortlich.
        </p>
        <p>
          Die verlinkten Seiten wurden zum Zeitpunkt der Verlinkung auf mögliche Rechtsverstöße überprüft. Rechtswidrige Inhalte waren zum Zeitpunkt der Verlinkung nicht erkennbar. Eine permanente inhaltliche Kontrolle der verlinkten Seiten ist jedoch ohne konkrete Anhaltspunkte einer Rechtsverletzung nicht zumutbar. Bei Bekanntwerden von Rechtsverletzungen werden wir derartige Links umgehend entfernen.
        </p>
      </LegalSection>

      <LegalSection title="Urheberrecht">
        <p>
          Die durch die Seitenbetreiber erstellten Inhalte und Werke auf diesen Seiten unterliegen dem deutschen Urheberrecht. Die Vervielfältigung, Bearbeitung, Verbreitung und jede Art der Verwertung außerhalb der Grenzen des Urheberrechts bedürfen der schriftlichen Zustimmung des jeweiligen Autors bzw. Erstellers.
        </p>
        <p>
          Downloads und Kopien dieser Seite sind nur für den privaten, nicht kommerziellen Gebrauch gestattet. Soweit die Inhalte auf dieser Seite nicht vom Betreiber erstellt wurden, werden die Urheberrechte Dritter beachtet. Insbesondere werden Inhalte Dritter als solche gekennzeichnet. Sollten Sie trotzdem auf eine Urheberrechtsverletzung aufmerksam werden, bitten wir um einen entsprechenden Hinweis. Bei Bekanntwerden von Rechtsverletzungen werden wir derartige Inhalte umgehend entfernen.
        </p>
      </LegalSection>

      <LegalSection title="Bildnachweise">
        <p>
          Verwendete Fotos: Unsplash (freie Lizenz für kommerzielle Nutzung). Icons: Lucide (ISC-Lizenz). Video- und Bildmaterial des Anbieters: {COMPANY.name}.
        </p>
      </LegalSection>
    </LegalPage>
  );
}
